import {
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import * as React from 'react';
import styled, { css } from 'styled-components';
import {
  type Shadow,
  colors,
  easingTokens,
  s32,
  shadows,
  transitionTokens,
} from '@ardoq/design-tokens';
import { OpenState, SidebarChevron } from './atoms';
import { SIDEBAR_WIDTH } from './PureSidebar';
import { ChevronAlignment, SidebarAlignment } from './types';
import { useKeydown } from '@ardoq/hooks';
import { useReturnFocusOnHide } from './useReturnFocusOnHide';
import { useWidthDrag } from './useWidthDrag';

const CLOSED_WIDTH = '16px';

const toSizeTransition = (isOpen: boolean): string =>
  isOpen
    ? `${transitionTokens.large} ${easingTokens.cubicOut}`
    : `${transitionTokens.medium} ${easingTokens.cubicInOut}`;
const OPEN_WIDTH = SIDEBAR_WIDTH;
const MIN_WIDTH = 160;
const MAX_WIDTH = 640;

const SidebarLayoutColumns = styled.div<{
  $shouldCoverContent?: boolean;
  $alignment: SidebarAlignment;
  $zIndex?: number;
}>`
  ${({ $shouldCoverContent = false, $alignment }) =>
    $shouldCoverContent &&
    css`
      position: fixed;
      ${$alignment === 'left' ? 'left: 0;' : 'right: 0;'}
    `}
  display: flex;
  /* Tells the layout that it's OK to shrink */
  min-height: 200px;
  height: 100%;
  ${({ $zIndex }) =>
    $zIndex &&
    css`
      z-index: ${$zIndex};
    `}
`;

/**
 * Handles reserving space for the sidebar among its neighbours
 */
type SidebarColumnProps = {
  $isOpen: boolean;
  $width: string;
  $isResizing: boolean;
};
const SidebarColumn = styled.div.attrs<SidebarColumnProps>(({ $width }) => ({
  style: {
    width: $width,
  },
}))<SidebarColumnProps>`
  position: relative;
  height: 100%;
  transition: width
    ${props => (props.$isResizing ? '0ms' : toSizeTransition(props.$isOpen))};
  overflow: visible;
  ${props => props.$isResizing && 'pointer-events: none; user-select: none'};
`;

const SidebarNeighbours = styled.div<{
  $isOpen: boolean;
  $shouldCoverContent?: boolean;
}>`
  flex: 1;
  overflow: auto;
  ${({ $isOpen, $shouldCoverContent = false }) =>
    $isOpen &&
    $shouldCoverContent &&
    css`
      background-color: #00000099;
      width: 100vw;
      transition: background-color ${$isOpen ? '300ms' : '150ms'} ease-in;
    `}
`;

/**
 * Handles displaying the sidebar at an appropriate size
 */
type SidebarDisplayProps = {
  $isOpen: boolean;
  $width: string;
  $hideSidebarWhenClosed: boolean;
  $alignment: SidebarAlignment;
  $isResizing: boolean;
  $shadow?: string;
};
const SidebarDisplay = styled.div.attrs<SidebarDisplayProps>(
  ({ $isOpen, $width }) => ({
    style: {
      width: $isOpen ? $width : 0,
    },
  })
)<SidebarDisplayProps>`
  position: relative;
  height: 100%;
  padding-right: ${({ $isOpen, $alignment, $hideSidebarWhenClosed }) =>
    $alignment === 'left' && !$hideSidebarWhenClosed && !$isOpen
      ? `${CLOSED_WIDTH}`
      : '0'};
  padding-left: ${({ $isOpen, $alignment, $hideSidebarWhenClosed }) =>
    $alignment === 'right' && !$hideSidebarWhenClosed && !$isOpen
      ? `${CLOSED_WIDTH}`
      : '0'};
  transition:
    width
      ${props => (props.$isResizing ? '0ms' : toSizeTransition(props.$isOpen))},
    padding-right ${props => toSizeTransition(props.$isOpen)},
    padding-left ${props => toSizeTransition(props.$isOpen)},
    visibility ${transitionTokens.medium};
  visibility: ${({ $isOpen, $hideSidebarWhenClosed }) =>
    $hideSidebarWhenClosed && !$isOpen ? 'hidden' : 'visible'};
  background-color: ${colors.surfaceStatusNeutral};
  box-shadow: ${({ $shadow }) =>
    $shadow || `0px 2px 4px 0px ${colors.borderAction}`};
  z-index: 1;
`;

const ChevronWrapper = styled.div<{
  $alignment: SidebarAlignment;
  $chevronAlignment: ChevronAlignment;
  $isResizing: boolean;
}>`
  position: absolute;
  height: 24px;
  width: 24px;
  top: ${props =>
    props.$chevronAlignment === 'middle' ? 'calc(50% - 12px)' : s32};
  ${props => (props.$alignment === 'left' ? 'right: -9px;' : 'left: -9px;')}
  z-index: 2;
  cursor: pointer;
  ${props => props.$isResizing && 'pointer-events: none; user-select: none'};
  i:hover {
    background-color: ${colors.surfaceHover} !important;
  }
`;

const SidebarClip = styled.div`
  overflow: hidden;
  height: 100%;
`;

const ResizeColumn = styled.div<{ $alignment: SidebarAlignment }>`
  position: absolute;
  top: 0;
  bottom: 0;
  ${props => (props.$alignment === 'left' ? 'right: -5px;' : 'left: -5px;')}
  width: 10px;
  cursor: col-resize;
  z-index: 1;
`;

export type SidebarLayoutProps = {
  isOpen?: boolean;
  onOpen?: VoidFunction;
  onClose?: VoidFunction;
  chevronAlignment?: ChevronAlignment;
  noChevron?: boolean;
  alignment?: SidebarAlignment;
  hideSidebarWhenClosed?: boolean;
  children: ReactNode;
  resizable?: boolean;
  onResizeEnd?: (width: string) => void;
  width?: string;
  initialWidth?: string;
  minWidth?: number;
  maxWidth?: number;
  shouldCoverContent?: boolean;
  shouldCloseOnExternalClick?: boolean;
  shouldCloseOnEscKeyPress?: boolean;
  zIndex?: number;
  shadow?: Shadow;
};

/**
 * Position a sidebar next to other content.
 *
 * The first child should be the <Sidebar> component; other children will be
 * placed to the side of the sidebar.
 */
const SidebarLayout = ({
  isOpen,
  onOpen,
  onClose,
  chevronAlignment = 'top',
  noChevron = false,
  alignment = 'left',
  resizable,
  initialWidth,
  width,
  minWidth = MIN_WIDTH,
  maxWidth = MAX_WIDTH,
  onResizeEnd,
  hideSidebarWhenClosed = false,
  shouldCoverContent,
  shadow,
  children,
  shouldCloseOnExternalClick,
  shouldCloseOnEscKeyPress,
  zIndex,
}: SidebarLayoutProps) => {
  const [openState, setOpenState] = useState<OpenState>(
    isOpen ? 'open' : 'closed'
  );
  const [sidebarWidth, setSidebarWidth] = useState<string>(
    initialWidth || width || OPEN_WIDTH
  );
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  useReturnFocusOnHide(
    hideSidebarWhenClosed && openState !== 'open',
    sidebarRef
  );

  useEffect(() => {
    setOpenState(isOpen ? 'open' : 'closed');
  }, [isOpen]);

  const openSidebar = useCallback(() => {
    setOpenState('open');
    onOpen?.();
  }, [onOpen]);

  const closeSidebar = useCallback(() => {
    setOpenState('closed');
    onClose?.();
  }, [onClose]);

  const handleChevronClick = useCallback(() => {
    if (openState !== 'open') {
      openSidebar();
    } else {
      closeSidebar();
    }
  }, [openState, closeSidebar, openSidebar]);

  useKeydown((event: KeyboardEvent) => {
    if (
      shouldCloseOnEscKeyPress &&
      openState === 'open' &&
      event.key === 'Escape'
    ) {
      closeSidebar();
    }
  });

  const chevRef = useRef<HTMLDivElement | null>(null);

  const handleSidebarHover = (e: MouseEvent, nextOpenState: OpenState) => {
    if (nextOpenState === openState) return;

    if (
      openState === 'open' ||
      (chevRef.current && chevRef.current.contains(e.target as Node))
    )
      return;

    setOpenState(nextOpenState);
  };

  // Work out what goes in which column
  const neighbours = React.Children.toArray(children);
  const sidebar = neighbours.shift();

  const endResize = (newWidth: number) => {
    setSidebarWidth(`${newWidth}px`);
    onResizeEnd?.(`${newWidth}px`);
  };
  const {
    dragWidth,
    isDragging: isResizing,
    startDrag: startResizing,
  } = useWidthDrag({
    panelRef: sidebarRef,
    alignment,
    resize: resizable
      ? { minWidth, maxWidth, onResizeEnd: endResize }
      : undefined,
  });
  const currentWidth =
    width || (dragWidth === null ? sidebarWidth : `${dragWidth}px`);

  return (
    <SidebarLayoutColumns
      $shouldCoverContent={shouldCoverContent}
      $alignment={alignment}
      $zIndex={zIndex}
    >
      {alignment === 'right' && (
        <SidebarNeighbours
          data-test-id="right-sidebar-neighbours"
          $isOpen={openState !== 'closed'}
          $shouldCoverContent={shouldCoverContent}
          onClick={() => shouldCloseOnExternalClick && onClose?.()}
        >
          {neighbours}
        </SidebarNeighbours>
      )}
      <SidebarColumn
        ref={sidebarRef}
        $width={
          openState === 'open'
            ? currentWidth
            : hideSidebarWhenClosed
              ? '0'
              : CLOSED_WIDTH
        }
        $isOpen={openState === 'open'}
        $isResizing={isResizing}
      >
        <SidebarDisplay
          $isOpen={openState !== 'closed'}
          $width={currentWidth}
          $hideSidebarWhenClosed={hideSidebarWhenClosed}
          $alignment={alignment}
          $isResizing={isResizing}
          onMouseEnter={e => handleSidebarHover(e, 'preview')}
          onMouseLeave={e => handleSidebarHover(e, 'closed')}
          $shadow={shadow ? shadows[shadow] : undefined}
        >
          {!hideSidebarWhenClosed && (
            <ChevronWrapper
              $chevronAlignment={chevronAlignment}
              $alignment={alignment}
              onClick={handleChevronClick}
              data-test-id="sidebar-chevron"
              ref={chevRef}
              $isResizing={isResizing}
            >
              {!noChevron && (
                <SidebarChevron alignment={alignment} openState={openState} />
              )}
            </ChevronWrapper>
          )}
          <SidebarClip>{sidebar}</SidebarClip>
          {resizable && openState === 'open' && (
            <ResizeColumn
              data-test-id="sidebar-resize-handle"
              onMouseDown={startResizing}
              $alignment={alignment}
            />
          )}
        </SidebarDisplay>
      </SidebarColumn>

      {alignment === 'left' && (
        <SidebarNeighbours
          $isOpen={openState !== 'closed'}
          $shouldCoverContent={shouldCoverContent}
          onClick={() => shouldCloseOnExternalClick && onClose?.()}
        >
          {neighbours}
        </SidebarNeighbours>
      )}
    </SidebarLayoutColumns>
  );
};

export { SidebarLayout };
